import { z } from 'zod';

export interface ManualPaymentState {
  amount: number;
  amountDue: number;
  outstandingBalance: number;
  minimumPayment: number;
  availableBalance: number;
  hasSelectedAccount: boolean;
  dueDate?: string;
}

export enum ManualPaymentStatus {
  OVER_PAYMENT = 'OVER_PAYMENT',
  BELOW_MINIMUM = 'BELOW_MINIMUM',
  LOW_BALANCE = 'LOW_BALANCE',
  PARTIAL = 'PARTIAL',
  NONE = 'NONE',
}

export interface ManualPaymentCaptionResult {
  status: ManualPaymentStatus;
  translationKey: string | null;
  params: Record<string, string | number>;
  isError: boolean;
}

// Schema for validating amount input from numeric keypad.
export const ManualPaymentAmountSchema = z
  .string()
  .refine((val) => val === '' || /^\d*(?:\.\d{0,2})?$/u.test(val), {
    message: 'Invalid amount format',
  });

export const ManualPaymentStateSchema = z.object({
  amount: z.number().min(0),
  amountDue: z.number().min(0),
  outstandingBalance: z.number().min(0),
  minimumPayment: z.number().min(0),
  availableBalance: z.number().min(0).optional(),
  hasSelectedAccount: z.boolean(),
  dueDate: z.string().optional(),
});

export function getManualPaymentStatus(
  state: ManualPaymentState,
): ManualPaymentStatus {
  const {
    amount,
    amountDue,
    outstandingBalance,
    minimumPayment,
    availableBalance,
    hasSelectedAccount,
    dueDate,
  } = state;
  if (amount < minimumPayment) {
    return ManualPaymentStatus.BELOW_MINIMUM;
  } else if (hasSelectedAccount && availableBalance < amount) {
    return ManualPaymentStatus.LOW_BALANCE;
  } else if (amount > outstandingBalance) {
    return ManualPaymentStatus.OVER_PAYMENT;
  } else if (amount > 0 && amount < amountDue && !!dueDate) {
    return ManualPaymentStatus.PARTIAL;
  }
  return ManualPaymentStatus.NONE;
}

export function buildManualPaymentCaption(
  state: ManualPaymentState,
): ManualPaymentCaptionResult {
  const parsed = ManualPaymentStateSchema.safeParse(state);
  if (!parsed.success) {
    return {
      status: ManualPaymentStatus.NONE,
      translationKey: null,
      params: {},
      isError: false,
    };
  }

  const status = getManualPaymentStatus(state);
  switch (status) {
    case ManualPaymentStatus.OVER_PAYMENT:
      return {
        status,
        translationKey: 'overPayment',
        params: { amount: state.outstandingBalance.toFixed(2) },
        isError: true,
      };
    case ManualPaymentStatus.BELOW_MINIMUM:
      return {
        status,
        translationKey: 'belowMinimumPayment',
        params: { minimumPayment: state.minimumPayment.toFixed(2) },
        isError: true,
      };
    case ManualPaymentStatus.LOW_BALANCE:
      return {
        status,
        translationKey: 'lowBalance',
        params: { amount: (state.availableBalance ?? 0).toFixed(2) },
        isError: true,
      };
    case ManualPaymentStatus.PARTIAL:
      return {
        status,
        translationKey: 'partialPayment',
        params: {
          remainingAmount: (state.amountDue - state.amount).toFixed(2),
          dueDate: state.dueDate ?? '',
        },
        isError: false,
      };
    case ManualPaymentStatus.NONE:
    default:
      return {
        status: ManualPaymentStatus.NONE,
        translationKey: null,
        params: {},
        isError: false,
      };
  }
}

export function getManualPaymentCaptionText(
  state: ManualPaymentState,
  translate: (key: string, params: Record<string, unknown>) => string,
  blankFallback = ' ',
): string {
  const result = buildManualPaymentCaption(state);
  if (!result.translationKey) {
    return blankFallback;
  }
  return translate(result.translationKey, result.params);
}
